// Shared constants — the single source of truth for enum-like values used across
// the API, CMS, player, and assistant. Keep this framework-free (no zod import)
// so it is cheap to import anywhere, including the zero-dependency player.

// --- Roles (see docs/08_SECURITY authorization table) ---------------------------
export const ROLES = ['owner', 'admin', 'editor', 'viewer'] as const;
export type Role = (typeof ROLES)[number];

// Higher number = more privilege. Used by assertRole(ctx, minRole) in services.
export const ROLE_RANK: Record<Role, number> = {
  viewer: 0,
  editor: 1,
  admin: 2,
  owner: 3,
};

// --- Member scopes (Sprint 7, docs/02 member_scopes) ----------------------------
// A scope row restricts an editor/viewer's writes to a specific screen or group. No
// rows (or an owner/admin) = org-wide, unchanged. See docs/08 §Scoped access.
export const SCOPE_TYPES = ['screen', 'group'] as const;
export type ScopeType = (typeof SCOPE_TYPES)[number];

// --- Plans & limits -------------------------------------------------------------
export const PLANS = ['trial', 'basic', 'pro', 'custom'] as const;
export type Plan = (typeof PLANS)[number];

// Shape of orgs.limits_json. Stored as JSON text in D1; parsed via planLimitsSchema.
export interface PlanLimits {
  max_screens: number;
  max_storage_mb: number;
  assistant: boolean;
  // Content Engine C-3 (docs/09 §B): per-org monthly AI generation cap. Optional in
  // stored limits; the generation service defaults to GEN_MONTHLY_DEFAULT when absent.
  gen_monthly?: number;
  // Sprint 11 T3: org-wide default manifest poll interval (seconds). Optional; absent →
  // DEFAULT_POLL_INTERVAL_S. Bounded [ORG_POLL_INTERVAL_MIN_S, ORG_POLL_INTERVAL_MAX_S].
  poll_interval_s?: number;
}

// Default monthly generation quota when limits_json omits gen_monthly (docs/09 §B).
export const GEN_MONTHLY_DEFAULT = 100;

// --- Audit provenance (audit_log.via) -------------------------------------------
// 'slack' (Sprint 5): a mutation driven by the assistant over the Slack bot surface —
// same agent loop and services as 'assistant', but the turn originated from Slack (a
// DM or an Apply button), so audit provenance can distinguish the two channels.
export const VIA = ['ui', 'assistant', 'mcp', 'api', 'slack'] as const;
export type Via = (typeof VIA)[number];

// --- Media ----------------------------------------------------------------------
// html_template / html_snippet are the Content Engine kinds (docs/09 §B): self-contained
// sandboxed HTML stored in R2. The kind/status columns are TEXT, so these are additive.
export const MEDIA_KINDS = ['image', 'video', 'url', 'html_template', 'html_snippet'] as const;
export type MediaKind = (typeof MEDIA_KINDS)[number];

// 'draft' is a not-yet-approved generation output (docs/09 §B2/E): excluded from normal
// media lists, deleted on reject or by the 7-day sweep, flipped to 'ready' on approve.
export const MEDIA_STATUSES = ['uploading', 'ready', 'error', 'draft'] as const;
export type MediaStatus = (typeof MEDIA_STATUSES)[number];

// The HTML content kinds render in the player's sandboxed iframe (docs/09 §B1/§B3).
export const HTML_MEDIA_KINDS = ['html_template', 'html_snippet'] as const;

// --- Manifest item types (docs/04 §Playback, docs/09 §B1) -----------------------
// The player's RENDERING contract — deliberately smaller than MEDIA_KINDS. 'html'
// covers BOTH Content Engine kinds (html_template and html_snippet): the player
// treats them identically, as one self-contained document. It is distinct from
// 'url' because the two are delivered differently (MANIFEST_ITEM_DELIVERY below).
export const MANIFEST_ITEM_TYPES = ['image', 'video', 'url', 'html'] as const;
export type ManifestItemType = (typeof MANIFEST_ITEM_TYPES)[number];

/**
 * media.kind → the manifest item type the resolver emits. THE source of truth for
 * that mapping: services/playlist-items.ts maps with it, and the player-side guard
 * tests derive the set of types they must handle from it, so neither side can drift.
 */
export const MEDIA_KIND_TO_MANIFEST_TYPE: Record<MediaKind, ManifestItemType> = {
  image: 'image',
  video: 'video',
  url: 'url',
  html_template: 'html',
  html_snippet: 'html',
};

/**
 * How the player must obtain each item type — part of the wire contract, not a
 * player implementation detail:
 *   'cached' → download via fetch (which CAN carry the device-token Authorization
 *              header), store in the media cache, play from a blob object-URL.
 *              Offline-capable; required for everything served from the
 *              token-authed /v1/player/media/:id route.
 *   'live'   → hand the url straight to the element, fetched by the browser on
 *              every play. Only legal for absolute http(s) pages needing no auth.
 * An <iframe>/<img> cannot send request headers, so a token-authed url marked
 * 'live' renders as a BLANK PAGE on every real device (the html-media P0 of
 * Jul 29, 2026 — see tasks/lessons.md). Adding a type here forces a delivery
 * decision, and the player guard tests prove the player implements it.
 */
export const MANIFEST_ITEM_DELIVERY: Record<ManifestItemType, 'cached' | 'live'> = {
  image: 'cached',
  video: 'cached',
  html: 'cached',
  url: 'live',
};

// Hard size cap for a generated html_snippet (docs/09 §B3): reject anything larger.
export const HTML_SNIPPET_MAX_BYTES = 64 * 1024;

// A draft older than this is swept (deleted + R2 cleaned) by the daily cron (docs/09 §B).
export const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

// Default per-item playback duration when an item is added without an explicit one
// (the CMS playlist editor + the assistant bulk-add tool use the same rule): a video
// plays its own clip length (falling back to DEFAULT_VIDEO_DURATION_MS), anything else
// shows for DEFAULT_ITEM_DURATION_MS.
export const DEFAULT_ITEM_DURATION_MS = 10_000;
export const DEFAULT_VIDEO_DURATION_MS = 15_000;

/** Default duration (ms) for a media item added without one — matches single-add. */
export function defaultItemDurationMs(
  kind: MediaKind,
  mediaDurationMs: number | null | undefined,
): number {
  return kind === 'video'
    ? (mediaDurationMs ?? DEFAULT_VIDEO_DURATION_MS)
    : DEFAULT_ITEM_DURATION_MS;
}

// --- Screens --------------------------------------------------------------------
export const ORIENTATIONS = ['landscape', 'portrait'] as const;
export type Orientation = (typeof ORIENTATIONS)[number];

// --- Playlists ------------------------------------------------------------------
export const FITS = ['contain', 'cover', 'stretch'] as const;
export type Fit = (typeof FITS)[number];

// --- Overlays (docs/09 §C2) -----------------------------------------------------
// Corner the logo watermark is pinned to: bottom-right/left, top-right/left.
export const OVERLAY_POSITIONS = ['br', 'bl', 'tr', 'tl'] as const;
export type OverlayPosition = (typeof OVERLAY_POSITIONS)[number];

// Logo size (% of screen width) and opacity (%) bounds — shared by the zod schema,
// the assistant tool, and the player. Kept here so all three agree by construction.
export const OVERLAY_SIZE_PCT_MIN = 3;
export const OVERLAY_SIZE_PCT_MAX = 30;
export const OVERLAY_OPACITY_MIN = 10;
export const OVERLAY_OPACITY_MAX = 100;

// Fixed ticker scroll speed emitted in the manifest overlay contract (px/second).
// The overlays table stores no speed column; the player animates at this constant.
export const OVERLAY_TICKER_SPEED_PX_S = 60;

// --- Layouts (Sprint 9, docs/09 §C1) ---------------------------------------------
// Fixed multi-zone presets — NOT free-form zones (weak-hardware rule: bounded player
// engine instances). 'fullscreen' is today's single-zone behavior; the others split
// the screen into named rects. Zone ids are exactly 'main'|'side'|'ticker'.
export const LAYOUT_PRESETS = ['fullscreen', 'main_ticker', 'main_side', 'l_shape'] as const;
export type LayoutPreset = (typeof LAYOUT_PRESETS)[number];

export const ZONE_IDS = ['main', 'side', 'ticker'] as const;
export type ZoneId = (typeof ZONE_IDS)[number];

/** A zone rect, in integer percent of the screen (x/y = top-left origin). */
export interface ZoneRectPct {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface PresetZone {
  zone: ZoneId;
  rect: ZoneRectPct;
}

// The fixed rect layout per preset. Framework-free data (no zod) so the player can
// import it directly. `main` always stays schedule-resolved (docs/02 resolver);
// non-main zones play their assigned playlist directly (layouts.zones_json).
export const PRESET_ZONES: Record<LayoutPreset, readonly PresetZone[]> = {
  fullscreen: [{ zone: 'main', rect: { x: 0, y: 0, w: 100, h: 100 } }],
  main_ticker: [
    { zone: 'main', rect: { x: 0, y: 0, w: 100, h: 90 } },
    { zone: 'ticker', rect: { x: 0, y: 90, w: 100, h: 10 } },
  ],
  main_side: [
    { zone: 'main', rect: { x: 0, y: 0, w: 75, h: 100 } },
    { zone: 'side', rect: { x: 75, y: 0, w: 25, h: 100 } },
  ],
  l_shape: [
    { zone: 'main', rect: { x: 0, y: 0, w: 75, h: 90 } },
    { zone: 'side', rect: { x: 75, y: 0, w: 25, h: 90 } },
    { zone: 'ticker', rect: { x: 0, y: 90, w: 100, h: 10 } },
  ],
};

/** The non-main zone ids a preset requires a layout's zones_json to assign a playlist to. */
export function nonMainZonesForPreset(preset: LayoutPreset): Exclude<ZoneId, 'main'>[] {
  return PRESET_ZONES[preset]
    .filter((z): z is PresetZone & { zone: Exclude<ZoneId, 'main'> } => z.zone !== 'main')
    .map((z) => z.zone);
}

// --- Schedules ------------------------------------------------------------------
export const TARGET_TYPES = ['screen', 'group', 'org'] as const;
export type TargetType = (typeof TARGET_TYPES)[number];

// A schedule's kind (schedules.kind, migration 0010). 'regular' is every
// operator-created schedule; 'broadcast' is an org-wide takeover created ONLY by the
// broadcast service (services/broadcast.ts) — never via the schedules CRUD.
export const SCHEDULE_KINDS = ['regular', 'broadcast'] as const;
export type ScheduleKind = (typeof SCHEDULE_KINDS)[number];

// Sprint 10 (docs/03 §Broadcast): the fixed priority a broadcast takeover is created at.
// A regular schedule is validated to priority ≤ BROADCAST_PRIORITY − 1 (9999), so a live
// broadcast always wins the resolver's max-priority tie-break over any normal schedule.
export const BROADCAST_PRIORITY = 10000;
export const MAX_REGULAR_SCHEDULE_PRIORITY = BROADCAST_PRIORITY - 1;

// Specificity used by the manifest resolver tie-break: screen > group > org.
export const TARGET_SPECIFICITY: Record<TargetType, number> = {
  org: 0,
  group: 1,
  screen: 2,
};

// --- Scheduled reports (Sprint 8, docs/03 §Report schedules) --------------------
// A report_schedules row emits a periodic Slack digest for a scope (org/group/screen).
// scope_type reuses TARGET_TYPES (same specificity concept as playlist schedules).
export const REPORT_FREQS = ['daily', 'weekly'] as const;
export type ReportFreq = (typeof REPORT_FREQS)[number];

// Sprint 10: 'attention' renders the CURRENT proactive attention list (services/attention.ts)
// at send time — not a historical metric over the report period like the other three.
export const REPORT_METRICS = ['uptime', 'playback', 'top_media', 'attention'] as const;
export type ReportMetric = (typeof REPORT_METRICS)[number];

// Only 'slack' ships in Sprint 8; email is a later channel on the same table.
export const REPORT_CHANNELS = ['slack'] as const;
export type ReportChannel = (typeof REPORT_CHANNELS)[number];

// A schedule is not re-run within this window of its last_run_at, even if the
// due-window math matches again (dedupe guard for the hourly cron — docs/03).
export const REPORT_DEDUPE_WINDOW_MS = 20 * 60 * 60 * 1000;

// --- Pairing codes (see docs/02 pairing_codes, docs/08 code space) --------------
// 6 chars, alphabet A-Z0-9 with ambiguous glyphs removed (no O/0/I/1).
// 24 letters + 8 digits = 32 symbols → 32^6 ≈ 1.07e9 codes.
export const PAIRING_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export const PAIRING_CODE_LENGTH = 6;

// --- Identifiers ----------------------------------------------------------------
// nanoid length used for all row ids (see docs/02: "all ids are nanoid 21").
export const ID_LENGTH = 21;

// --- Player protocol ------------------------------------------------------------
export const DEFAULT_POLL_INTERVAL_S = 60;
export const MANIFEST_HORIZON_MS = 24 * 60 * 60 * 1000; // resolver looks 24h ahead
export const PAIRING_CODE_TTL_MS = 15 * 60 * 1000; // codes expire in 15 min

// Sprint 11 T3 (docs/04 §Traffic diet): bounds for an org's optional manifest poll-cadence
// override (orgs.limits_json.poll_interval_s). Only the polling FALLBACK — a screen with a
// live WS connection is on a much longer stretch regardless (see docs/04). Per-screen
// granularity is explicitly deferred (no free column on `screens`, no migrations this sprint).
export const ORG_POLL_INTERVAL_MIN_S = 30;
export const ORG_POLL_INTERVAL_MAX_S = 900;
